/* eslint-disable no-useless-escape */
import { Form, Formik, FormikValues } from "formik";
import Container from "./Container";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import FormField from "./FormField";
import Text from "./Text";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useRef } from "react";
import { Toast } from "primereact/toast";

const API_URL = import.meta.env.VITE_API_URL;

interface ISignUpFormProps {
    onSignIn: () => void;
}

export default function SignUpForm({ onSignIn }: ISignUpFormProps) {
    const toast = useRef<Toast | null>(null);
    const formInitialValues = {
        email: "",
        password: ""
    };

    const signUpMutation = useMutation({
        mutationFn: async ({ email, password, name } :{ email: string; password: string; name: string; }) => {
            try {
                return await axios.post(
                    `${API_URL}/user/signup`, 
                    { 
                        username: email, 
                        email, 
                        password, 
                        name 
                    },
                    {
                        headers: {
                            "Content-Type": "text/plain"
                        }
                    });
            } catch (err) {
                const message = (err as { response: { data: string; } }).response.data.split("ERRO:");
                throw new Error(message[message.length - 1]);
            }
        },
        onSuccess: () => {
            toast.current?.show({
                severity: "success",
                summary: "Usuário cadastrado com sucesso",
            });

            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    });
    
    return (
        <>
            <Toast ref={toast} />

            <Container flexColumn fullWidth maxWidth={500} paddingHorizontal="6" paddingVertical="3">
                <Formik
                    initialValues={formInitialValues}
                    enableReinitialize
                    validationSchema={Yup.object().shape({
                        name: Yup.string()
                            .required("Campo nome obrigatório"),
                        email: Yup.string()
                            .email("Valor informado não é um email")
                            .required("Campo email obrigatório"),
                        password: Yup.string()
                            .required("Campo senha obrigatório")
                            .min(8, "Mínimo 8 caracteres")
                            .matches(/[A-Z]/, "Mínimo uma letra maiúscula")
                            .matches(/[a-z]/, "Mínimo uma letra minúscula")
                            .matches(/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/, "Mínimo um caracter especial")
                    })}
                    onSubmit={async (values: FormikValues) => {
                        await signUpMutation.mutateAsync({email: values.email, password: values.password, name: values.name});
                    }}
                >
                    {
                        () => (
                            <Form>
                                <FormField 
                                    label="Nome completo" 
                                    id="name" 
                                    type="text"
                                />

                                <FormField 
                                    label="Endereço de Email" 
                                    id="email" 
                                    type="email"
                                />

                                <FormField 
                                    label="Senha" 
                                    id="password" 
                                    type="password"
                                    secureText
                                />

                                <span className="block text-500 font-light text-xs mb-1">Mínimo 8 caracteres</span>
                                <span className="block text-500 font-light text-xs my-1">Mínimo uma letra maiúscula</span>
                                <span className="block text-500 font-light text-xs my-1">Mínimo uma letra minúscula</span>
                                <span className="block text-500 font-light text-xs mt-1 mb-3">Mínimo um caracter especial</span>

                                <Button 
                                    label={signUpMutation.isPending ? "Cadastrando..." : "Cadastrar"} 
                                    type="submit" 
                                    loading={signUpMutation.isPending}
                                    className="bg-primary w-full" 
                                />

                                {
                                    signUpMutation?.error && <Message className="mt-2 w-full" severity="error" text={signUpMutation.error.message} />
                                }

                                <Text center size="xs" className="mt-2" color="primary" onClick={() => onSignIn()}>
                                    Já tem uma conta? Entre
                                </Text>
                            </Form>
                        )
                    }
                </Formik>
            </Container>
        </>
    )
}